import { useState, useEffect } from 'react';
import { Wrench, Image, MessageSquare, TrendingUp } from 'lucide-react';
import { getStats, getQuotes } from '@/lib/api';

export const DashboardOverview = () => {
  const [stats, setStats] = useState({
    services: 0,
    gallery: 0,
    quotes: 0,
    unread_quotes: 0,
  });
  const [recentQuotes, setRecentQuotes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, quotesRes] = await Promise.all([
          getStats(),
          getQuotes(),
        ]);
        setStats(statsRes.data);
        setRecentQuotes(quotesRes.data.slice(0, 5));
      } catch (error) {
        console.error('Error fetching stats:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  const statCards = [
    { label: 'Usługi', value: stats.services, icon: Wrench, color: 'bg-blue-500' },
    { label: 'Galeria', value: stats.gallery, icon: Image, color: 'bg-green-500' },
    { label: 'Zapytania', value: stats.quotes, icon: MessageSquare, color: 'bg-purple-500' },
    { label: 'Nieprzeczytane', value: stats.unread_quotes, icon: TrendingUp, color: 'bg-amber-500' },
  ];

  return (
    <div data-testid="dashboard-overview">
      <div className="mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">Przegląd</h1>
        <p className="text-slate-400">Witaj w panelu administracyjnym CAM VISION</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statCards.map((stat) => (
          <div
            key={stat.label}
            className="bg-[#111827] rounded-xl p-6 border border-cyan-500/10 card-hover stats-card"
            data-testid={`stat-${stat.label.toLowerCase()}`}
          >
            <div className="flex items-center justify-between mb-4">
              <div className={`w-12 h-12 ${stat.color} rounded-xl flex items-center justify-center`}>
                <stat.icon className="w-6 h-6 text-white" />
              </div>
              <span className="text-3xl font-bold text-white">{stat.value}</span>
            </div>
            <p className="text-slate-400 font-medium">{stat.label}</p>
          </div>
        ))}
      </div>

      {/* Recent Quotes */}
      <div className="bg-[#111827] rounded-xl border border-cyan-500/10 overflow-hidden">
        <div className="px-6 py-4 border-b border-cyan-500/10">
          <h2 className="text-lg font-semibold text-white">Ostatnie zapytania</h2>
        </div>
        <div className="divide-y divide-cyan-500/10">
          {recentQuotes.length === 0 ? (
            <div className="px-6 py-8 text-center text-slate-400">
              Brak zapytań
            </div>
          ) : (
            recentQuotes.map((quote) => (
              <div
                key={quote.id}
                className="px-6 py-4 hover:bg-cyan-500/5 transition-colors flex items-center justify-between"
                data-testid={`quote-${quote.id}`}
              >
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-white">{quote.name}</p>
                    {!quote.is_read && (
                      <span className="px-2 py-0.5 bg-cyan-500/20 text-cyan-400 text-xs font-medium rounded-full badge-pulse">
                        Nowe
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-slate-400 mt-1">{quote.service_type}</p>
                </div>
                <p className="text-sm text-slate-500">
                  {new Date(quote.created_at).toLocaleDateString('pl-PL')}
                </p>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardOverview;
